import { Mail, Pencil, RotateCcw, Search, Trash2, X } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { doctorSignupService } from '../api/services/doctor-signup.service'
import { reviewerService } from '../api/services/reviewer.service'
import { userService } from '../api/services/user.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { ListPagination } from '../components/ui/list-pagination'
import { SelectPicker, type SelectOption } from '../components/ui/select-picker'
import { SoftDeleteConfirmDialog } from '../components/ui/soft-delete-confirm-dialog'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { useAuth } from '../context/AuthContext'
import {
  REVIEWER_SALUTATION_OPTIONS,
  type Reviewer,
  type ReviewerFormPayload,
} from '../types/reviewer'

type ActiveFilter = 'all' | 'active' | 'disabled'
type CanReviewFilter = 'all' | 'yes' | 'no'

const ACTIVE_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Inactive' },
]

const CAN_REVIEW_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All review access' },
  { value: 'yes', label: 'Can review' },
  { value: 'no', label: 'Cannot review' },
]

const reviewerSchema = z.object({
  salutation: z.string().min(1),
  first_name: z.string().min(1, 'First name is required'),
  surname: z.string().min(1, 'Surname is required'),
  email: z.string().email('Valid email is required'),
  phone_number: z.string().optional(),
  position_id: z.string().optional(),
  can_review_profiles: z.boolean(),
  is_active: z.boolean(),
})

type ReviewerFormValues = z.infer<typeof reviewerSchema>

const emptyForm: ReviewerFormValues = {
  salutation: 'dr',
  first_name: '',
  surname: '',
  email: '',
  phone_number: '',
  position_id: '',
  can_review_profiles: true,
  is_active: true,
}

export function ReviewersPage() {
  const { can } = useAuth()
  const [reviewers, setReviewers] = useState<Reviewer[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [withTrashed, setWithTrashed] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [canReviewFilter, setCanReviewFilter] = useState<CanReviewFilter>('all')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Reviewer | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Reviewer | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [positionOptions, setPositionOptions] = useState<SelectOption[]>([])
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [meta, setMeta] = useState<PaginationMeta | null>(null)

  const form = useForm<ReviewerFormValues>({
    resolver: zodResolver(reviewerSchema),
    defaultValues: emptyForm,
  })

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (search.trim()) count += 1
    if (activeFilter !== 'all') count += 1
    if (canReviewFilter !== 'all') count += 1
    if (withTrashed) count += 1
    return count
  }, [activeFilter, canReviewFilter, search, withTrashed])

  const clearFilters = () => {
    setSearch('')
    setSearchInput('')
    setActiveFilter('all')
    setCanReviewFilter('all')
    setWithTrashed(false)
  }

  const loadReviewers = useCallback(async () => {
    try {
      const response = await reviewerService.list({
        page,
        per_page: perPage,
        only_trashed: withTrashed || undefined,
        search: search.trim() || undefined,
        is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
        can_review_profiles:
          canReviewFilter === 'all' ? undefined : canReviewFilter === 'yes',
      })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setReviewers(items)
      setMeta(nextMeta)
    } catch {
      setReviewers([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [activeFilter, canReviewFilter, page, perPage, search, withTrashed])

  useEffect(() => {
    void loadReviewers()
  }, [loadReviewers])

  useEffect(() => {
    setPage(1)
  }, [activeFilter, canReviewFilter, search, withTrashed])

  // Positions come from the public signup form-data endpoint (shared reference data).
  useEffect(() => {
    void doctorSignupService
      .getFormData()
      .then((response) => {
        setPositionOptions(
          (response.data?.positions ?? []).map((position) => ({
            value: position.id,
            label: position.name,
          })),
        )
      })
      .catch(() => {
        setPositionOptions([])
      })
  }, [])

  const openCreate = () => {
    setEditing(null)
    form.reset(emptyForm)
    setFormOpen(true)
  }

  const openEdit = (reviewer: Reviewer) => {
    setEditing(reviewer)
    form.reset({
      salutation: reviewer.salutation,
      first_name: reviewer.first_name,
      surname: reviewer.surname,
      email: reviewer.email,
      phone_number: reviewer.phone_number ?? '',
      position_id: reviewer.position_id ?? '',
      can_review_profiles: reviewer.can_review_profiles,
      is_active: reviewer.is_active,
    })
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditing(null)
    form.reset(emptyForm)
  }

  const onSubmit = async (values: ReviewerFormValues) => {
    setSubmitting(true)

    const payload: ReviewerFormPayload = {
      salutation: values.salutation,
      first_name: values.first_name,
      surname: values.surname,
      email: values.email,
      phone_number: values.phone_number || undefined,
      position_id: values.position_id || undefined,
      can_review_profiles: values.can_review_profiles,
      is_active: values.is_active,
    }

    try {
      if (editing) {
        await reviewerService.update(editing.id, payload)
      } else {
        await reviewerService.create(payload)
      }

      closeForm()
      await loadReviewers()
    } catch {
      // Errors toasted globally by axios interceptor.
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return
    }

    setDeleting(true)

    try {
      await reviewerService.remove(deleteTarget.id)
      setDeleteTarget(null)
      await loadReviewers()
    } catch {
      // handled globally
    } finally {
      setDeleting(false)
    }
  }

  const handleRestore = async (reviewer: Reviewer) => {
    try {
      await reviewerService.restore(reviewer.id)
      await loadReviewers()
    } catch {
      // handled globally
    }
  }

  const handleResendCredentials = async (reviewer: Reviewer) => {
    setResendingId(reviewer.id)

    try {
      await userService.resendCredentials(reviewer.id)
    } catch {
      // handled globally
    } finally {
      setResendingId(null)
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Profile reviewers</h1>
            <p className="mt-1 text-sm text-ink-600">
              Manage Profile Reviewer accounts. Passwords are auto-generated and emailed — never
              entered here.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-600"
                checked={withTrashed}
                onChange={(event) => setWithTrashed(event.target.checked)}
              />
              Show Deleted
            </label>
            <PermissionGate permission="reviewers-create">
              <Button onClick={openCreate}>Add Reviewer</Button>
            </PermissionGate>
          </div>
        </div>

        <section className="mb-6 rounded-xl border border-parch-100 bg-white p-4 shadow-card sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-ink-900">Filters</h2>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-semibold text-green-700">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-stretch gap-2">
            <div className="relative min-w-[12rem] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
              <Input
                className="pl-9"
                placeholder="Search name, email…"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setSearch(searchInput.trim())
                  }
                }}
              />
            </div>
            <Button onClick={() => setSearch(searchInput.trim())}>Search</Button>
            {activeFilterCount > 0 && (
              <Button variant="secondary" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel>Status</FieldLabel>
              <SelectPicker
                options={ACTIVE_OPTIONS}
                value={activeFilter}
                onChange={(value) => setActiveFilter(value as ActiveFilter)}
                placeholder="All"
              />
            </div>
            <div>
              <FieldLabel>Can review</FieldLabel>
              <SelectPicker
                options={CAN_REVIEW_OPTIONS}
                value={canReviewFilter}
                onChange={(value) => setCanReviewFilter(value as CanReviewFilter)}
                placeholder="All review access"
              />
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Can review</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && reviewers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-sm text-ink-500">
                      No reviewers found.
                    </td>
                  </tr>
                ) : (
                  reviewers.map((reviewer) => (
                    <tr key={reviewer.id} className="border-b border-parch-50 last:border-0">
                      <td className="px-4 py-3">
                        {reviewer.first_name} {reviewer.surname}
                      </td>
                      <td className="px-4 py-3">{reviewer.email}</td>
                      <td className="px-4 py-3">{reviewer.can_review_profiles ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">
                        {reviewer.deleted_at ? (
                          <span className="text-danger">Deleted</span>
                        ) : reviewer.is_active ? (
                          <span className="text-green-700">Active</span>
                        ) : (
                          <span className="text-ink-500">Disabled</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <TableIconActions>
                          {can('reviewers-update') && !reviewer.deleted_at && (
                            <IconActionButton
                              icon={Pencil}
                              label="Edit"
                              onClick={() => openEdit(reviewer)}
                            />
                          )}
                          {can('users-resend-credentials') && !reviewer.deleted_at && (
                            <IconActionButton
                              icon={Mail}
                              label="Resend Creds Email"
                              disabled={resendingId === reviewer.id}
                              onClick={() => void handleResendCredentials(reviewer)}
                            />
                          )}
                          {can('reviewers-delete') && !reviewer.deleted_at && (
                            <IconActionButton
                              icon={Trash2}
                              label="Delete"
                              variant="destructive"
                              onClick={() => setDeleteTarget(reviewer)}
                            />
                          )}
                          {can('reviewers-restore') && reviewer.deleted_at && (
                            <IconActionButton
                              icon={RotateCcw}
                              label="Restore"
                              onClick={() => void handleRestore(reviewer)}
                            />
                          )}
                        </TableIconActions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n)
            setPage(1)
          }}
        />

        <SoftDeleteConfirmDialog
          open={deleteTarget !== null}
          entityLabel="reviewer"
          itemName={
            deleteTarget
              ? `${deleteTarget.first_name} ${deleteTarget.surname}`.trim()
              : ''
          }
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirm()}
          confirming={deleting}
        />

        {formOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-xl rounded-lg bg-white p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">
                {editing ? 'Edit Reviewer' : 'Create Reviewer'}
              </h2>
              {!editing && (
                <p className="mt-1 text-sm text-ink-600">
                  A strong password (8+ chars, upper/lower/number/symbol) will be generated and
                  emailed automatically.
                </p>
              )}

              <form className="mt-5 space-y-3" onSubmit={form.handleSubmit(onSubmit)}>
                <FormRequiredLegend />

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <FieldLabel required>First name</FieldLabel>
                    <Input {...form.register('first_name')} />
                    {form.formState.errors.first_name && (
                      <p className="mt-1 text-xs text-danger">
                        {form.formState.errors.first_name.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <FieldLabel required>Surname</FieldLabel>
                    <Input {...form.register('surname')} />
                    {form.formState.errors.surname && (
                      <p className="mt-1 text-xs text-danger">
                        {form.formState.errors.surname.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <FieldLabel required>Salutation</FieldLabel>
                    <select
                      className="w-full rounded-md border border-parch-200 px-3 py-2 text-sm"
                      {...form.register('salutation')}
                    >
                      {REVIEWER_SALUTATION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <FieldLabel optional>Position</FieldLabel>
                    <SelectPicker
                      options={positionOptions}
                      value={form.watch('position_id') || ''}
                      onChange={(value) => form.setValue('position_id', value)}
                      placeholder="Select position"
                    />
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <FieldLabel required>Email</FieldLabel>
                    <Input type="email" {...form.register('email')} />
                    {form.formState.errors.email && (
                      <p className="mt-1 text-xs text-danger">
                        {form.formState.errors.email.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <FieldLabel optional>Phone number</FieldLabel>
                    <Input {...form.register('phone_number')} />
                  </div>
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" {...form.register('can_review_profiles')} />
                    Allowed to review doctor profiles
                  </label>

                  {editing && (
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" {...form.register('is_active')} />
                      Account active
                    </label>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="secondary" onClick={closeForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Reviewer'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
